import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import Button from "../../atoms/button";
import type { TournamentApplyInput } from "../../../types/tournament.types";
import { cn } from "../../../lib/utils/cn";
import type { CalendarItem } from "../../../@apis/dto/response/torunament.response";
import { formatDate } from "../../../lib/utils/dateUtils";
import {
  genderLabel,
  unavailableScheduleMessage,
} from "../../../lib/utils/tournamentUtils";
import { useMyInfoStore } from "../../../lib/store/useMyInfoStore";

interface DateSelectProps {
  calendarList: CalendarItem[];
}

export default function CompetitonDateScreen({
  calendarList,
}: DateSelectProps) {
  const { setValue, watch } = useFormContext<TournamentApplyInput>();
  const { myInfo } = useMyInfoStore();
  // "0"/0 여성, "1"/1 남성. 숫자로 내려와도 같은 분기로 본다.
  const isFemale = myInfo.gender == "0";
  const isMale = myInfo.gender == "1";

  // 선택 가능한 일정이 1건이면 선택된 상태로 둔다. 그 1건이 마감이면 선택하지 않는다.
  useEffect(() => {
    if (calendarList.length !== 1) return;
    const only = calendarList[0];
    const full = only.isFull ?? only.currentApplyCount >= only.maxApplyCount;
    if (full) return;
    setValue("calendarId", only.id);
  }, [calendarList, setValue]);

  return (
    <section>
      <h1 className="font-semibold  mb-[36px]">
        참가하실 대회 날짜를 선택해 주세요.
      </h1>

      {calendarList.length === 0 ? (
        <p className="text-sm font-medium text-[#8e8e93] break-keep">
          {unavailableScheduleMessage(myInfo.gender)}
        </p>
      ) : (
        <div className="space-y-[12px]">
          {calendarList.map(
            ({
              id,
              date,
              timeType,
              maxApplyCount,
              currentApplyCount,
              gender,
              isFull,
            }) => {
              // 정원 마감 여부 (isFull 우선, 없으면 현재/최대 비교)
              const full = isFull ?? currentApplyCount >= maxApplyCount;

              return (
                <Button
                  key={id}
                  className={cn(
                    "py-[12px] px-[20px] w-full",
                    watch("calendarId") == id && "border-1 border-black",
                    // 마감: 회색 배경 + 선택 불가
                    full && "bg-[#f2f2f7] cursor-not-allowed",
                  )}
                  variant={"white"}
                  label=""
                  onClick={() => {
                    if (full) return;
                    setValue("calendarId", id);
                  }}
                >
                  <div className="flex justify-between items-center w-full gap-x-[8px]">
                    {/* 왼쪽 */}
                    <div
                      className={cn(
                        // min-w-0: 일정 문구가 길어도 오른쪽 인원수를 밀어내지 않도록 한다
                        "flex flex-1 min-w-0 flex-col gap-y-[8px]",
                        full && "opacity-30",
                      )}
                    >
                      <p className="text-sm font-medium whitespace-pre-wrap break-words flex w-full gap-x-[8px] items-center">
                        <img src="/icons/ic-calendar.webp" className="size-5" />
                        {formatDate(date, "MM월 DD일 (ddd)")}
                      </p>
                      <p className="text-sm font-medium flex w-full gap-x-[8px] items-center whitespace-pre-line break-all">
                        <span
                          className={cn(
                            "px-[4px] rounded-[5px] whitespace-nowrap",
                            {
                              "bg-[#fff8da] text-[#a2845e]": gender === null,
                              "bg-[#ffdada] text-[#ff3b30]": gender === 0,
                              "bg-[#daeaff] text-[#007aff]": gender === 1,
                            },
                          )}
                        >
                          {genderLabel(gender)}
                        </span>
                        {timeType}
                      </p>
                    </div>

                    {(maxApplyCount ?? 0) < 100000 && (
                      <div
                        className={cn(
                          "flex shrink-0 flex-col items-end gap-y-[2px]",
                          full && "opacity-30",
                        )}
                      >
                        {(isFemale || isMale) && (
                          <span
                            className={cn(
                              "text-xs font-medium",
                              isFemale && "text-[#ff3b30]",
                              isMale && "text-[#007aff]",
                            )}
                          >
                            {isFemale ? "여성" : "남성"}
                          </span>
                        )}
                        <small className="text-black text-sm font-medium">
                          <span
                            className={full ? "text-[#ff3b30]" : "text-[#34c759]"}
                          >
                            {currentApplyCount}
                          </span>
                          /{maxApplyCount.toLocaleString()}명
                        </small>
                      </div>
                    )}
                  </div>
                </Button>
              );
            },
          )}
        </div>
      )}
    </section>
  );
}
